import React, { Component } from 'react';
import find from 'lodash/find';
import isNumber from 'lodash/isNumber';
import includes from 'lodash/includes';
import Data from '../data';
import UI from '../ui';
import Utils from '../utils';
import RateAdvisorUtil from './RateAdvisorUtil';
import ServiceOptionsIncluded from './ServiceOptionsIncluded.component';

const { AllCarriers, Carrier } = Data;
const { WrappedButton, WrappedTooltip } = UI;
const { Session, Util } = Utils;

class ServiceHeader extends Component {
	onPriceButtonClick = e => {
		const { serviceData, onServiceSelection } = this.props;

		e.stopPropagation(); // prevents the event from bubbling up the DOM
		e.preventDefault(); // prevents the default behaviour of navigating to the location in the href

		onServiceSelection(serviceData);
	};

	/**
	 * Render HTML superscript Tag in the DOM when string contains '®' or
	 * '<sup>&reg;</sup>'.
	 * @param {string} serviceName Service name to display.
	 * @returns {ReactDOM}
	 */
	renderServiceName = serviceName => {
		const pattern = '<sup>&reg;</sup>';
		const patternStartIndex = includes(serviceName, pattern)
			? serviceName.indexOf(pattern)
			: -1;

		if (patternStartIndex !== -1) {
			return (
				<>
					{serviceName.substring(0, patternStartIndex)}
					<sup>&reg;</sup>
					{serviceName.substring(patternStartIndex + pattern.length)}
				</>
			);
		}

		return serviceName;
	};

	/**
	 * Render included options text depending on the rate and carrier-services data.
	 * @param {boolean} isServiceInfoExpanded `true` when the service info section is expanded.
	 * @param {object} serviceData Processed service data see See processRates method in RateAdvisorUtil class.
	 * @returns {ReactDOM}
	 */
	renderServiceOptionsIncludedSection = (isServiceInfoExpanded, serviceData) => {
		const includedServices = RateAdvisorUtil.getIncludedServiceOptions(serviceData);
		const tooltipTitle = this.renderServiceName(`Included with ${serviceData.serviceName}:`);
		const tooltipMockup = (
			<>
				<h4 className="title">{tooltipTitle}</h4>
				<ServiceOptionsIncluded iconSize="small" data={includedServices} />
			</>
		);

		return (
			<div className="service-included">
				<p>{includedServices.map(option => option.text).join(', ')}</p>

				{!isServiceInfoExpanded && (
					<>
						<p className={`view-details-${serviceData.key}`}>View Details</p>
						<WrappedTooltip
							target={`view-details-${serviceData.key}`}
							position="bottom"
							className="common-rate-advisor-tootip"
						>
							{tooltipMockup}
						</WrappedTooltip>
					</>
				)}
			</div>
		);
	};

	/**
	 * Render estimated delivery days text depending on rate object. Server could return
	 * null, or "{x}", or "{x}-{y}". This method will render "Varies", or "{x} Day(s)"
	 * or "{x}-{y} Days", respectively.
	 * See processRates method in RateAdvisorUtil class.
	 * @param {number|null} minDeliverDays
	 * @param {number|null} maxDeliverDays
	 * @returns {string}
	 */
	renderEstimatedDeliveryDays = (minDeliverDays, maxDeliverDays) => {
		// server return null => [1,999]
		if (minDeliverDays === 1 && maxDeliverDays === 999) {
			return 'Varies';
		}

		if (!isNumber(maxDeliverDays)) {
			return `${minDeliverDays} ${minDeliverDays > 1 ? 'Days' : 'Day'}`;
		}

		return `${minDeliverDays}-${maxDeliverDays} Days`;
	};

	render() {
		const { serviceData, isActive } = this.props;
		const { carrier, serviceName, packageName, minDeliverDays, maxDeliverDays } = serviceData;

		// calculate carrier icon classname
		const carrierData = find(AllCarriers, ['value', carrier]);
		const carrierIconCls = carrierData ? `${carrierData.key.toLowerCase()}-logo` : '';

		/**
		 * SERA Rates API returns the total_amount which includes all surcharges
		 * including residential_delivery_surcharge; therefore, we do not need
		 * to do extra calculation we do with SWSIM Rates.
		 */
		const amount = isNumber(serviceData?.shipmentCost?.totalAmount)
			? serviceData.shipmentCost.totalAmount
			: 0;
		const serviceAmount = Util.formatCurrency(amount);

		// display upgrade required text for USPS if user is on Freemium plan
		const upgradeRequired = carrier === Carrier.USPS && Session.isFreemiumUser();

		return (
			<div className="common-rate-advisor-service-header">
				<div className={`service-icon ${carrierIconCls}`} />

				<div className="service-description">
					<p className="service-name">{this.renderServiceName(serviceName)}</p>
					<p className="package-name">{packageName}</p>
					{this.renderServiceOptionsIncludedSection(isActive, serviceData)}
				</div>

				<div className="service-eta">
					{this.renderEstimatedDeliveryDays(minDeliverDays, maxDeliverDays)}
				</div>
				<WrappedButton
					className="service-amount"
					label={serviceAmount}
					dataqa="services-cost-button"
					onClick={this.onPriceButtonClick}
					segmentvalue={serviceName}
				>
					{upgradeRequired && <p className="upgrade-required">Upgrade required</p>}
				</WrappedButton>
			</div>
		);
	}
}

export default ServiceHeader;
